import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { feedbackService, FeedbackData } from '../services/feedbackService';

type PanelView = 'menu' | 'search' | 'feedback';

type SearchItem = {
  title: string;
  path: string;
  type: 'page' | 'tool' | 'resource';
  description: string;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  allowedRoles?: string[];
  external?: boolean;
};

const SEARCHABLE: SearchItem[] = [
  { title: 'Home', path: '/', type: 'page', description: 'Nexus gateway — find tools and programme' },
  {
    title: "EuFMD's Programme",
    path: '/programme',
    type: 'page',
    description: 'Move FAST 2023–2027 progress, key areas, and country activities',
  },
  {
    title: 'Fast Report',
    path: '/fast-report',
    type: 'tool',
    description: 'Report disease outbreaks',
  },
  {
    title: 'Risk Monitoring Tool',
    path: '/RMT',
    type: 'tool',
    description: 'Monitor disease risks',
  },
  {
    title: 'RMT Risk Scores',
    path: '/rmt/risk-scores',
    type: 'tool',
    description: 'Risk Monitoring Tool — risk scores',
  },
  {
    title: 'RMT Results',
    path: '/rmt/results',
    type: 'tool',
    description: 'Risk Monitoring Tool — results',
  },
  {
    title: 'PCP-FMD Map',
    path: '/PCP-fmd-map',
    type: 'tool',
    description: 'FMD Progressive Control Pathway Map',
  },
  {
    title: 'VADEMOS',
    path: '/Vademos',
    type: 'tool',
    description: 'Vaccine Demand Estimation Model',
  },
  {
    title: 'Get Prepared Wall',
    path: '/get-prepared-wall',
    type: 'resource',
    description: 'Prepare for animal disease outbreaks',
  },
  {
    title: 'Get Prepared',
    path: '/getprepared',
    type: 'page',
    description: 'Preparedness resources',
  },
  {
    title: 'Emergency Toolbox',
    path: '/emergency-toolbox',
    type: 'resource',
    description: 'Emergency response toolkit',
  },
  {
    title: 'Emergency Response',
    path: '/emergency-response',
    type: 'page',
    description: 'Emergency response resources',
  },
  {
    title: 'Training',
    path: '/training',
    type: 'page',
    description: 'Training courses and capacity development',
  },
  {
    title: 'Training Impact',
    path: '/view-past-training-impact',
    type: 'tool',
    description: 'Past training impact data',
  },
  {
    title: 'Monitoring',
    path: '/monitoring',
    type: 'page',
    description: 'Monitoring overview',
  },
  {
    title: 'TOM',
    path: 'https://eufmd-tom.com:9443',
    type: 'resource',
    description: 'Training management (external)',
    external: true,
  },
  {
    title: 'RISP',
    path: '/risp',
    type: 'tool',
    description: 'Real-time Information System for Progress',
    requiresAuth: true,
    allowedRoles: ['admin', 'risp'],
  },
  {
    title: 'RISP Outbreak',
    path: '/risp/outbreak',
    type: 'tool',
    description: 'RISP outbreak entry',
    requiresAuth: true,
    allowedRoles: ['admin', 'risp'],
  },
  {
    title: 'RISP Vaccination',
    path: '/risp/vaccination',
    type: 'tool',
    description: 'RISP vaccination data',
    requiresAuth: true,
    allowedRoles: ['admin', 'risp'],
  },
  {
    title: 'RISP Surveillance',
    path: '/risp/surveillance',
    type: 'tool',
    description: 'RISP surveillance data',
    requiresAuth: true,
    allowedRoles: ['admin', 'risp'],
  },
  {
    title: 'RISP Market Price',
    path: '/risp/market-price',
    type: 'tool',
    description: 'RISP market price data',
    requiresAuth: true,
    allowedRoles: ['admin', 'risp'],
  },
  {
    title: 'SOI',
    path: '/risp/soi',
    type: 'tool',
    description: 'Statement of Intent (SOI)',
    requiresAuth: true,
    allowedRoles: ['admin', 'soi'],
  },
  {
    title: 'RISP Summary',
    path: '/risp/summary',
    type: 'tool',
    description: 'RISP summary views',
    requiresAuth: true,
    allowedRoles: ['admin', 'risp'],
  },
  {
    title: 'Thrace',
    path: '/thrace',
    type: 'tool',
    description: 'Thrace surveillance activities',
    requiresAuth: true,
    allowedRoles: ['admin', 'thrace'],
  },
  {
    title: 'Training Credits',
    path: '/training-credits',
    type: 'tool',
    description: 'Manage training credits',
    requiresAuth: true,
    allowedRoles: ['admin', 'tfp'],
  },
  {
    title: 'PCP FMD',
    path: '/PCP_fmd',
    type: 'tool',
    description: 'PCP FMD data management',
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    title: 'Diagnostic Support',
    path: '/diagnostic-support',
    type: 'tool',
    description: 'Diagnostic support tools',
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    title: 'RMT Data',
    path: '/RMTData',
    type: 'tool',
    description: 'Risk Monitoring Tool data administration',
    requiresAuth: true,
    requiresAdmin: true,
  },
];

function canAccessItem(
  item: SearchItem,
  isAuthenticated: boolean,
  role: string | undefined
): { ok: boolean; reason?: string } {
  const roleLower = (role || '').toLowerCase();
  const isAdmin = roleLower === 'admin';

  // Admins can open every tool and resource
  if (isAuthenticated && isAdmin) {
    return { ok: true };
  }

  if (item.requiresAdmin || item.requiresAuth || item.allowedRoles?.length) {
    if (!isAuthenticated) {
      return { ok: false, reason: 'This resource is for logged-in users. Please sign in.' };
    }
  }

  if (item.requiresAdmin) {
    return { ok: false, reason: 'This resource is for administrators only.' };
  }

  if (item.allowedRoles?.length) {
    if (!item.allowedRoles.map((r) => r.toLowerCase()).includes(roleLower)) {
      return { ok: false, reason: 'This resource is only available for authorised roles.' };
    }
  }

  return { ok: true };
}

const CowAssistant: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();
  const rootRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PanelView>('menu');

  const [query, setQuery] = useState('');
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return SEARCHABLE.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.type.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setView('menu');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const resetFeedback = () => {
    setSelectedRating(null);
    setComment('');
  };

  const closePanel = () => {
    setOpen(false);
    setView('menu');
    setQuery('');
    resetFeedback();
  };

  const toggleOpen = () => {
    if (open) closePanel();
    else {
      setOpen(true);
      setView('menu');
    }
  };

  const handleSubmitFeedback = async () => {
    if (selectedRating === null) return;
    setIsSubmitting(true);
    try {
      const feedbackData: FeedbackData = {
        score: selectedRating,
        comment: comment.trim() || undefined,
        page: location.pathname,
        country: user?.country || undefined,
        user_id: user?.id || undefined,
      };
      await feedbackService.submitFeedback(feedbackData);
      setShowSuccess(true);
      resetFeedback();
      setView('menu');
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={rootRef} className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
      {showSuccess && (
        <div className="mb-1 bg-[#15736d] text-white text-sm px-3 py-2 rounded-xl shadow-lg">
          Thank you for your feedback!
        </div>
      )}

      {open && (
        <div className="w-[300px] max-w-[calc(100vw-2.5rem)] rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden origin-bottom-right animate-[fadeIn_0.15s_ease-out]">
          <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-[#15736d] text-white">
            <div className="flex items-center gap-2 min-w-0">
              {view !== 'menu' && (
                <button
                  type="button"
                  onClick={() => {
                    setView('menu');
                    setQuery('');
                    resetFeedback();
                  }}
                  className="shrink-0 rounded-full p-1 hover:bg-white/15"
                  aria-label="Back"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <p className="text-sm font-semibold truncate">
                {view === 'menu' && 'How can I help?'}
                {view === 'search' && 'Search tools & resources'}
                {view === 'feedback' && 'Give feedback'}
              </p>
            </div>
            <button
              type="button"
              onClick={closePanel}
              className="shrink-0 rounded-full p-1 hover:bg-white/15"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-3 max-h-[min(58vh,360px)] overflow-y-auto">
            {view === 'menu' && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setView('search')}
                  className="w-full text-left rounded-xl border border-gray-200 hover:border-[#15736d]/50 hover:bg-[#f4faf9] px-3 py-2.5 transition-colors"
                >
                  <div className="text-sm font-semibold text-gray-900">Search tools or resources</div>
                  <div className="text-xs text-gray-500 mt-0.5">Find pages and tools, then open the link</div>
                </button>
                <button
                  type="button"
                  onClick={() => setView('feedback')}
                  className="w-full text-left rounded-xl border border-gray-200 hover:border-[#15736d]/50 hover:bg-[#f4faf9] px-3 py-2.5 transition-colors"
                >
                  <div className="text-sm font-semibold text-gray-900">Give feedback</div>
                  <div className="text-xs text-gray-500 mt-0.5">Tell us how your experience was</div>
                </button>
              </div>
            )}

            {view === 'search' && (
              <div className="space-y-2">
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Type what you need…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#15736d] focus:border-[#15736d]"
                />
                {query.trim() && filtered.length === 0 && (
                  <p className="text-xs text-gray-500 px-1">No matches. Try another word.</p>
                )}
                <ul className="space-y-1">
                  {filtered.map((result) => {
                    const access = canAccessItem(result, !!isAuthenticated, user?.role);
                    if (!access.ok) {
                      return (
                        <li key={result.path}>
                          <div className="rounded-lg px-2.5 py-2 border border-amber-100 bg-amber-50/80">
                            <div className="text-sm font-medium text-gray-900">{result.title}</div>
                            <div className="text-[11px] text-amber-800 mt-0.5">{access.reason}</div>
                            <Link
                              to="/login"
                              onClick={closePanel}
                              className="inline-block mt-1.5 text-[11px] font-semibold text-[#15736d] hover:underline"
                            >
                              Go to login
                            </Link>
                          </div>
                        </li>
                      );
                    }
                    if (result.external) {
                      return (
                        <li key={result.path}>
                          <a
                            href={result.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closePanel}
                            className="block rounded-lg px-2.5 py-2 hover:bg-[#f4faf9] border border-transparent hover:border-[#d8ebe8]"
                          >
                            <div className="text-sm font-medium text-gray-900">{result.title}</div>
                            <div className="text-[11px] text-gray-500">{result.description}</div>
                          </a>
                        </li>
                      );
                    }
                    return (
                      <li key={result.path}>
                        <Link
                          to={result.path}
                          onClick={closePanel}
                          className="block rounded-lg px-2.5 py-2 hover:bg-[#f4faf9] border border-transparent hover:border-[#d8ebe8]"
                        >
                          <div className="text-sm font-medium text-gray-900">{result.title}</div>
                          <div className="text-[11px] text-gray-500">{result.description}</div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {view === 'feedback' && (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-sm text-gray-600">Your rating</span>
                    {selectedRating !== null && (
                      <span className="text-sm font-medium text-[#15736d]">{selectedRating}/10</span>
                    )}
                  </div>
                  <div className="flex gap-1 flex-wrap justify-center mb-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setSelectedRating(rating)}
                        className={`w-7 h-7 rounded-full text-[11px] font-medium transition-colors ${
                          selectedRating === rating
                            ? 'bg-[#15736d] text-white'
                            : selectedRating !== null && rating <= selectedRating
                              ? 'bg-green-100 text-[#15736d]'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                        title={`Rate ${rating}/10`}
                      >
                        {rating}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-center text-gray-500 min-h-[1rem]">
                    {selectedRating !== null && selectedRating <= 3 && 'Poor experience'}
                    {selectedRating !== null &&
                      selectedRating >= 4 &&
                      selectedRating <= 6 &&
                      'Average experience'}
                    {selectedRating !== null &&
                      selectedRating >= 7 &&
                      selectedRating <= 8 &&
                      'Good experience'}
                    {selectedRating !== null && selectedRating >= 9 && 'Excellent experience'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Additional comments (optional)
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    placeholder="Tell us more about your experience..."
                    className="w-full text-sm p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#15736d] focus:border-[#15736d]"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setView('menu');
                      resetFeedback();
                    }}
                    className="flex-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold py-2"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitFeedback}
                    disabled={isSubmitting || selectedRating === null}
                    className="flex-1 rounded-lg bg-[#15736d] hover:bg-[#0f5b57] text-white text-sm font-semibold py-2 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting…' : 'Submit feedback'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        aria-label={open ? 'Close assistant' : 'Open assistant'}
        className={`group relative w-24 h-24 sm:w-28 sm:h-28 rounded-full shadow-lg border-2 border-white bg-[#f4faf9] overflow-hidden transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#15736d] focus:ring-offset-2 ${
          open ? 'ring-2 ring-[#15736d]' : ''
        }`}
      >
        <img
          src={`${process.env.PUBLIC_URL}/cow.png`}
          alt=""
          className="w-full h-full object-cover"
        />
      </button>
    </div>
  );
};

export default CowAssistant;
